// Copy and photos for the 13 legacy 3D printing city pages
// (/3d-printing-service-{city}/), rendered by PrintingCityPage.js. They were
// Contentful page-template pages until November 2026; gatsby-node skips the
// Contentful entries for the slugs in `citySlugs`.
//
// Facts are the /services/3d-printing/ ones (printing-service.js and
// .seo/truth.md): from about $30, 3D modeling and scanning from $250, one
// print to production runs on the in-house FDM print farm, no setup fee,
// next-day printing for many jobs. Anything else is quoted per project.
//
// Owner rule 3 (.seo/brand.md, 2026-11-01): city pages test demand for future
// locations, so they name the city and the service only. No distance framing
// (where the studio is, "shipped from", shipping times) and no claim of a
// local storefront or address. Delivery is "to your site", quoted to the ZIP
// code. Same-day is never promised. The owner said the studio has many
// clients in San Francisco, Los Angeles and Houston; the other pages don't
// claim local clients. Gallery photos carry no client names, and the old
// "projects for our clients in {city}" claim is gone.
const printing = require('./printing-service')

const { pricing } = printing
const photoRoot = 'https://images.ctfassets.net/dovsnf6qrcem'
const reviewedDate = '2026-11-04'

// Studio photos from the old Contentful city galleries, plus the national
// and NYC page photos. Licensed characters and named clients are left out.
const photos = {
  ...printing.photos,
  ...require('./printing-nyc').photos,
  // National photos used in the work grids need a title and a caption.
  dispenser: {
    ...printing.photos.dispenser,
    title: 'Dispenser prototype',
    caption: 'An open-front dispenser printed to test size and fit.',
  },
  gear: {
    ...printing.photos.gear,
    title: 'Stainless-steel impeller',
    caption: 'A solid stainless-steel part printed by laser melting.',
  },
  architecture: {
    ...printing.photos.architecture,
    title: 'Multi-level building model',
    caption: 'An architectural scale model printed in Somos resin.',
  },
  sculpture: {
    ...printing.photos.sculpture,
    title: 'Resin warrior sculpture',
    caption: 'Fine-detail resin, sharp enough to show every strand.',
  },
  vitruvian: {
    url: `${photoRoot}/4yoxLpUrU2tcw8osBOIJmn/99d4a6da7810d661b542e886526f7143/LAL_4332.jpg`,
    width: 7360,
    height: 4912,
    alt: 'A white 3D printed Vitruvian Man sculpture inside a ring',
    title: 'Vitruvian Man sculpture',
    caption: 'A fine-detail resin sculpture, straight from the printer.',
  },
  goldSpheres: {
    url: `${photoRoot}/2NjUe33HGTGAvsbEDmIQMO/02f1d4fdaaf20cf12b5769296c766799/LAL_5320.jpg`,
    width: 6992,
    height: 4617,
    alt: 'Six 3D printed spheres with a raised pattern, plated in gold',
    title: 'Gold-plated spheres',
    caption: 'Textured spheres, printed in resin and plated in gold.',
  },
  redCreature: {
    url: `${photoRoot}/6jxKg9n6hIqAj4fcvSSC5i/d482b343263c5ae69be7f4abebd4a49b/LAL_4460.jpg`,
    width: 4476,
    height: 6707,
    alt: 'A painted red 3D printed creature figure standing on a white base',
    title: 'Painted creature figure',
    caption: 'A resin figure, sanded and painted glossy red.',
  },
  nylonParts: {
    url: `${photoRoot}/5Qo4T4IVJEeYsaCSacr0JW/4476fa9d015fdad0b5781749792519ca/LAL_6957.jpeg`,
    width: 5551,
    height: 4912,
    alt: 'Black 3D printed nylon brackets, arms and a finned part laid out on white',
    title: 'Black nylon parts',
    caption: 'Brackets, arms and a finned part printed in black nylon.',
  },
  greyParts: {
    url: `${photoRoot}/5eItAzbE8EURILjXMdtvW0/096988e64371e473d87418513e64b583/LAL_8377.jpeg`,
    width: 3000,
    height: 2002,
    alt: 'Grey 3D printed parts: a hub, a tube, a nose cone, lattice pieces and small figures',
    title: 'Mixed grey test prints',
    caption:
      'A hub, a nose cone, lattice parts and figures from one print run.',
  },
  containers: {
    url: `${photoRoot}/4ezY6zjifGZXbYWgv0HJoF/a72cd1ae51ab743aa2983b2d7f5f5462/_DSC2954.jpg`,
    width: 5000,
    height: 3396,
    alt: 'Five white 3D printed containers and cups on a black surface',
    title: 'Container prototypes',
    caption: 'Cup and container prototypes, printed to check shape and size.',
  },
  appliance: {
    url: `${photoRoot}/3J0d2V1R5NOJCOpUsr4CBR/8384f953abffecd46bddafc43943baa6/LAL_7974.jpeg`,
    width: 3000,
    height: 2002,
    alt: 'A white 3D printed appliance housing prototype on a black base',
    title: 'Appliance housing prototype',
    caption: 'A full-size housing printed as a looks-like prototype.',
  },
  diorama: {
    url: `${photoRoot}/6FDfg0iXivZuo1hUn4LkRZ/8ee21773944f1b35e82c48194acfee3e/LAL_8224.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A highly detailed grey resin 3D printed vehicle diorama held in a hand',
    title: 'Fine-detail resin diorama',
    caption: 'Small parts and sharp edges in grey resin, held in the hand.',
  },
  dino: {
    url: `${photoRoot}/6kBjIaBjcm1SCSLFhF0Ban/a9801a87b3580951484c858a24f4f77e/acrylic_keychain-9.jpg`,
    width: 2002,
    height: 3000,
    alt: 'A pink 3D printed dinosaur figure mounted on a wooden base',
    title: 'Dinosaur figure on wood',
    caption: 'A painted dinosaur figure mounted on a wood base.',
  },
  pets: {
    url: `${photoRoot}/3ZC9srB7MD0tWzJ3730Ogl/70171c7d571f56f3459788fb634c4230/LAL_8349.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A painted brown dog figurine next to a rainbow-colored corgi figure',
    title: 'Painted pet figures',
    caption: 'Two pet figures, printed in resin and painted by hand.',
  },
  spiderRobot: {
    url: `${photoRoot}/7x3lyFE8a91jUayEkuTh45/31b2a569d81218af6bd2908be9d42f8d/LAL_8196.jpg`,
    width: 3000,
    height: 2255,
    alt: 'A detailed grey 3D printed four-legged robot model held in a hand',
    title: 'Walking robot model',
    caption: 'A multi-part robot model with thin legs and fine detail.',
  },
  batch: {
    url: `${photoRoot}/71euFz7gTNGZIhukYaNybp/dffec7a81f89b3641b83d70b3956a8ec/mmexport1559995535068.jpg`,
    width: 1440,
    height: 1080,
    alt: 'A tray full of white 3D printed parts at the end of a print run',
    title: 'A full print run',
    caption: 'Dozens of white parts from one run, ready for cleanup.',
  },
  goldAwards: {
    url: `${photoRoot}/3zjpgsuPJN1QOBgmbI3A19/ba939a7d787c5ecba91c07fecaa510be/LAL_8152.jpg`,
    width: 3000,
    height: 2002,
    alt: 'Three gold-finished 3D printed figure awards on white bases with plates',
    title: 'Gold figure awards',
    caption: 'Figure awards printed, finished in gold and mounted on bases.',
  },
  elbows: {
    url: `${photoRoot}/55aDPIyCRvzD855MO5g8Y3/17f5450c85ffe80c5900be6ebdd21dac/LAL_7285.jpg`,
    width: 3000,
    height: 2002,
    alt: 'Two 3D printed flanged pipe elbows with a grey metallic finish',
    title: 'Flanged pipe elbows',
    caption: 'Ducting elbows with bolt flanges, printed to check fit.',
  },
  bigPrint: {
    url: `${photoRoot}/5V1kuYzssVbFO0mS4BO9ye/f31629f04e54e30d6c3a4065c4deadd2/LAL_7198.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A large white 3D printed part next to a smartphone for scale',
    title: 'Large white print',
    caption: 'A large one-piece print, with a phone for scale.',
  },
  copperMolding: {
    url: `${photoRoot}/2Du4IBKjq3A8Pxbbtb6RCB/04ac84633d4fd58d32e531782d990af0/LAL_4766.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A copper-plated 3D printed molding that breaks into a rough texture',
    title: 'Copper-plated molding study',
    caption: 'An architectural molding study, printed and copper plated.',
  },
  spiderStage: {
    url: `${photoRoot}/1ldwS9loBOgIfgW0u7dKbd/ac74a2cb523a302fafcc84a5a93d9fa0/LAL_8202.jpeg`,
    width: 3000,
    height: 2177,
    alt: 'A grey 3D printed spider robot miniature with fine mechanical detail',
    title: 'Spider robot miniature',
    caption: 'A miniature with thin legs and fine mechanical detail.',
  },
  earbuds: {
    url: `${photoRoot}/1JAtigRibxs9yBvgqVWBKf/e81e50ec55c888cdad8a77be4b1df79d/_DSC3286.jpg`,
    width: 7360,
    height: 4912,
    alt: 'Earbud case models 3D printed in navy, teal, blue, yellow, red and black',
    title: 'Earbud case colorways',
    caption: 'One case design printed and painted in six colorways.',
  },
  painting: {
    url: `${photoRoot}/3UbPvKE2bMPrPxnUJwsbeJ/162346c45815577208525b8fc60c6cd7/LAL_8086.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A PrintAWorld team member hand painting a batch of 3D printed figures in the studio',
    title: 'Painting in the studio',
    caption: 'A batch of printed figures being hand painted in the studio.',
  },
  supports: {
    url: `${photoRoot}/22AitMPtbUxaNoK2FwHuwB/ad9330bb9c88fb4782bd8d9822eb31a8/LAL_8271.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A white resin 3D print still attached to its build plate by lattice supports',
    title: 'Straight off the printer',
    caption: 'A resin print with its supports still on, before cleanup.',
  },
  bronzeDiorama: {
    url: `${photoRoot}/4hYYVXDDQy6cSLlLgKJgHO/bb0e8e313d92529de780f21f097310d0/LAL_8475.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A bronze-finished 3D printed diorama of figures on lounge chairs',
    title: 'Bronze-finished diorama',
    caption: 'A detailed diorama, printed and finished in bronze.',
  },
  bronzeModel: {
    url: `${photoRoot}/5ROsHuPwtfG8Aa1rVwNg1/04e8a4ff16bc466e9de8cc542e2f27da/LAL_8601.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A bronze-finished 3D printed architectural model of a building',
    title: 'Bronze architectural model',
    caption: 'An architectural model with a bronze finish.',
  },
  miniatures: {
    url: `${photoRoot}/7BP7aTRTX0zGbhPutGPLeD/4e0b5b9edffc2f98604f127a12a378c7/IMG_5697.jpg`,
    width: 1125,
    height: 1113,
    alt: 'Two grey resin 3D printed warrior miniatures on round bases',
    title: 'Resin warrior miniatures',
    caption: 'Two warrior miniatures in grey resin, ready for paint.',
  },
  cupcakes: {
    url: `${photoRoot}/4iZxZFC8sTOTMstTm1GG7m/9c5f1e88eb7c75d11c85fdf2ccf318db/LAL_6779.jpg`,
    width: 7360,
    height: 4590,
    alt: 'Four painted 3D printed cupcake sculptures on white saucers',
    title: 'Cupcake sculptures',
    caption: 'Dessert sculptures printed, then painted with red cherries.',
  },
  chair: {
    url: `${photoRoot}/2HUFG8LJ63B1ovcJZWVNRD/1d635bd233063cbafd6526027c743125/L1040158.jpg`,
    width: 3000,
    height: 2000,
    alt: 'A chrome-plated 3D printed art chair on display at an event',
    title: 'Chrome art chair',
    caption: 'Art furniture, printed and metal plated for a show.',
  },
  bust: {
    url: `${photoRoot}/6z34876l6xdfbJAS77bbQz/474c50f6a685f7c6a4beb763338d6311/LAL_6884.jpg`,
    width: 4912,
    height: 7360,
    alt: 'A 3D printed mechanical bust with a metallic painted finish',
    title: 'Metallic mechanical bust',
    caption: 'A sculpted bust with a hand-painted metallic finish.',
  },
  failArt: {
    url: `${photoRoot}/3L6gizxyk2dnivOSHlo2Ln/4dd5c1741602890e868d66979d844b96/3d-print-fail-art-min.jpg`,
    width: 2091,
    height: 3000,
    alt: 'A blue 3D print shown with its support structure as an art piece',
    title: 'Print-and-support art piece',
    caption: 'A blue print kept with its supports, shown as an art piece.',
  },
  goldTrophies: {
    url: `${photoRoot}/70PZRNWEfH94cm6ujgKF0F/1b266a24e16a3588d6057c7bc72aabca/LAL_6821.jpg`,
    width: 4394,
    height: 5172,
    alt: 'Two tall 3D printed trophies plated in gold',
    title: 'Gold-plated trophies',
    caption: 'Tall sculpted trophies, printed and plated in gold.',
  },
  earrings: {
    url: `${photoRoot}/5xiTKhphoEqmJrAFRoVqX9/65fb0ba489cf0b10330c1a7df3426fac/3d-printed-victoria-hayes-earrings-2-min.jpg`,
    width: 3000,
    height: 2002,
    alt: 'Rows of bright 3D printed logo earrings in pink, yellow and blue',
    title: 'Logo earrings',
    caption: 'Logo earrings printed in a run of bright colors.',
  },
}

const timingFaq = (name) => ({
  question: `How long does 3D printing take for a ${name} project?`,
  answer:
    'Many jobs can be printed next day. Your written quote confirms the delivery date, based on the file, size, material, quantity, finishing and where it’s going. Same-day isn’t standard. Large-format builds usually take 1–2 weeks, and bigger, complex projects can take 1–2 months. Tell us your deadline when you ask for a quote.',
})

const baseFaqs = (name) => [
  {
    question: `Where can I get something 3D printed in ${name}?`,
    answer: `You can use a library, college or makerspace printer, an instant-quote website, or a 3D printing studio like PrintAWorld. We review your file or photos, send a written quote, print and finish it in-house, and deliver it to your site in ${name}. There’s no setup fee, and prints start around $${pricing.printFrom}. For a cheap hobby print, a makerspace or a hobbyist will usually cost less.`,
  },
  {
    question: `How much does 3D printing cost for a ${name} project?`,
    answer: `At PrintAWorld, 3D printing starts around $${pricing.printFrom} for a simple print from your file and goes up to thousands of dollars for large or finished pieces. Size, material, machine time, quantity and finishing set the price, and delivery is quoted to your ZIP code. 3D modeling starts at $${pricing.modelingFrom} if you don’t have a file. There’s no setup fee.`,
  },
  timingFaq(name),
  {
    question: 'Can I order just one 3D print?',
    answer: `Yes. There’s no minimum order and no setup fee for 3D printing: one prototype is fine, and so is a large production order on our in-house FDM print farm. Small jobs start around $${pricing.printFrom}, and we quote runs per piece.`,
  },
  {
    question: 'Who is PrintAWorld’s 3D printing for?',
    answer: `Businesses, brands, agencies, architects and creative or production teams in ${name}: prototypes, display pieces, props, events and production runs. Because we’re a professional studio, we cost more than a hobbyist. For a one-off hobby print, or parts you plan to resell, a print marketplace, a makerspace or a hobbyist will usually be cheaper, and we’re happy to point you that way.`,
  },
  {
    question: 'What files do I need for 3D printing?',
    answer:
      'Send an STL, OBJ, STEP, STP or 3DM file when you have one. Other formats and file repairs are reviewed with your quote. If the file is too big to attach, send a download link.',
  },
  {
    question: 'Can you 3D print something without a 3D model?',
    answer: `Yes. We can model it from a sketch, photos or measurements. 3D modeling starts at $${pricing.modelingFrom}, and you approve the model before we print. To copy a real object, we can 3D scan it instead; scanning starts at $${pricing.scanningFrom}.`,
  },
]

// Each city: its own lead, intro, local buyers, library options, big-build
// delivery note and FAQs. `clients: true` only where the owner confirmed many
// local clients (San Francisco, Los Angeles, Houston).
const cityList = [
  {
    slug: 'houston',
    name: 'Houston',
    state: 'Texas',
    stateCode: 'TX',
    title: '3D Printing Houston, TX | Resin & Custom Parts',
    description: `3D printing in Houston for businesses and brands: prototypes, custom parts, resin prints and display models. One print to a full run, no setup fee, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · Houston, Texas',
    areas: 'Sugar Land, Katy, The Woodlands, Pearland and Clear Lake',
    clients: true,
    hero: 'vitruvian',
    work: [
      'elbows',
      'greyParts',
      'appliance',
      'bigPrint',
      'goldSpheres',
      'redCreature',
    ],
    lead: 'PrintAWorld 3D prints prototypes, tooling mockups, display models and production parts for Houston businesses, from energy and industrial suppliers to medical, aerospace and event teams.',
    intro: [
      'Houston buys 3D printing for work: a bracket or housing to check fit before it’s machined, a cutaway of a valve or tool for a sales team, a scale model of a plant or a building for a client meeting, a run of branded pieces for a conference booth. That’s the work we’re set up for.',
      'You send the file, a person checks it and replies with a written quote, and we print it in-house and deliver it to your site. Houston clients use us because we reply quickly and get projects done in a matter of days, including the finishing most print shops skip: sanding, painting and metal plating.',
    ],
    buyers: [
      {
        title: 'Energy and industrial suppliers',
        body: 'Fit-check prototypes of brackets, housings and tool parts in nylon or ABS, and painted cutaway or scale models for sales meetings and trade shows. We make prototypes and display pieces, not certified or pressure-rated parts.',
      },
      {
        title: 'Medical and life-science teams',
        body: 'Early device and enclosure prototypes, and presentation models for pitches and conferences around the Texas Medical Center. These are prototypes and models, not regulated medical production.',
      },
      {
        title: 'Aerospace, architecture and events',
        body: 'Display models for aerospace and space programs, architectural and site models for developers and design firms, and props and branded pieces for conventions and corporate events.',
      },
    ],
    library:
      'Harris County Public Library and Houston Public Library offer 3D printing and workshops at some branches, and some colleges have maker labs. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'Sending a display to the George R. Brown Convention Center or parts to a site office? Give us the address and the date you need it, and we’ll plan printing and delivery around it.',
    guide: '/guides/best-3d-printing-services-houston/',
    faqs: [
      {
        question: 'Can you 3D print oilfield or industrial parts?',
        answer:
          'We print fit-check prototypes, tooling mockups, jigs to try out, and display models of equipment in nylon, ABS, resin or stainless steel. We don’t make certified, pressure-rated or safety-critical production parts; for those you need a qualified supplier. Tell us how the part will be used and we’ll say honestly whether we’re the right fit.',
      },
      {
        question:
          'Can you make a display model or prop for a Houston trade show?',
        answer:
          'Yes. We print display models and props, including large pieces printed in sections and assembled, then sand, paint or metal plate them. Send the show date and venue with your file; printing, finishing and delivery are quoted together.',
      },
      {
        question: 'Can Houston medical or life-science teams order prototypes?',
        answer:
          'Yes, as prototypes and presentation models: device housings, enclosures and models for pitches and conferences. We don’t make regulated or patient-contact parts, and we don’t claim medical certifications.',
      },
    ],
  },
  {
    slug: 'dallas',
    name: 'Dallas',
    state: 'Texas',
    stateCode: 'TX',
    title: '3D Printing Dallas, TX | Prototypes & Custom Parts',
    description: `3D printing in Dallas–Fort Worth for brands, agencies and product teams: prototypes, custom parts and production runs. No setup fee, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · Dallas–Fort Worth, Texas',
    areas: 'Fort Worth, Plano, Irving, Richardson, Frisco and Arlington',
    hero: 'nylonParts',
    work: ['containers', 'prototype', 'diorama', 'pets', 'dino', 'goldAwards'],
    lead: 'PrintAWorld 3D prints prototypes, production parts, packaging models and branded display pieces for Dallas–Fort Worth companies, brands and agencies.',
    intro: [
      'DFW is full of companies that need a part made rather than a printer: headquarters launching a product, consumer brands testing a package, tech teams in Richardson and Plano checking an enclosure, agencies building a pop-up. We print one prototype or a production run on our in-house FDM print farm, with no setup fee.',
      'You send the file and get a written quote from a person. We print and finish it in-house and deliver it to your office, warehouse or venue anywhere in the Metroplex.',
    ],
    buyers: [
      {
        title: 'Corporate and consumer brands',
        body: 'Packaging and product prototypes, oversized product replicas and display pieces for launches, retail tests and sales meetings.',
      },
      {
        title: 'Tech and product teams',
        body: 'Enclosure and fit-check prototypes for teams along the Telecom Corridor in Richardson and Plano, and short production runs once the design is settled.',
      },
      {
        title: 'Agencies, events and real estate',
        body: 'Props and branded pieces for activations and conventions, and architectural and site models for developers and design firms across the Metroplex.',
      },
    ],
    library:
      'Dallas Makerspace is a member workshop with 3D printers, and some DFW colleges and libraries have maker labs. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'Fort Worth, Plano, Frisco or Arlington: the address is the only difference. We deliver to the door you give us, whether that’s an office, a warehouse or a venue.',
    faqs: [
      {
        question: 'I need a single item 3D printed in Dallas. Can you do it?',
        answer: `Yes. There’s no minimum order and no setup fee, so one prototype or one display piece is fine; simple prints start around $${pricing.printFrom}. If it’s a quick hobby print, Dallas Makerspace or a hobbyist will usually cost less, and that’s fine by us.`,
      },
      {
        question: 'Do you serve Fort Worth, Plano and the rest of DFW?',
        answer:
          'Yes. We deliver to any address in Dallas–Fort Worth, from Denton to Arlington, and quote delivery to your ZIP code with the print.',
      },
      {
        question: 'Can you print a production run for a Dallas brand?',
        answer:
          'Yes. Large production orders run on our in-house FDM print farm, and we quote runs per piece. Send the file, the quantity and the date, and say whether the parts need sanding, paint or packing.',
      },
    ],
  },
  {
    slug: 'san-francisco',
    name: 'San Francisco',
    state: 'California',
    stateCode: 'CA',
    title: '3D Printing San Francisco | Resin Prototypes & Parts',
    description: `3D printing in San Francisco for startups, design studios and brands: resin prototypes, enclosures and custom parts. One print to production runs, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · San Francisco Bay Area',
    areas:
      'Oakland, Berkeley, South San Francisco, Palo Alto and the rest of the Bay Area',
    clients: true,
    hero: 'batch',
    work: [
      'bigPrint',
      'spiderRobot',
      'greyParts',
      'dispenser',
      'goldAwards',
      'minis',
    ],
    lead: 'PrintAWorld 3D prints hardware prototypes, enclosures, display models and production parts for San Francisco startups, design studios and brands.',
    intro: [
      'Bay Area teams usually come to us between the desk printer and the factory: an enclosure that has to look finished for an investor demo, a run of parts for a pilot, a model for a launch event, a fixture to try on the line. A person checks your file, suggests the process and material, and prints it in-house.',
      'San Francisco clients stay with us because we reply quickly and get projects done in a matter of days, with sanding, painting and plating done by the same team that prints the part.',
    ],
    buyers: [
      {
        title: 'Hardware and robotics startups',
        body: 'Looks-like and works-like enclosure prototypes, fit-check parts and short runs for pilots and demos, in resin, nylon or ABS.',
      },
      {
        title: 'Biotech and lab teams',
        body: 'Fixtures, holders and presentation models for labs in South San Francisco and Mission Bay. Prototypes and models only, not regulated medical production.',
      },
      {
        title: 'Design studios, agencies and brands',
        body: 'Finished product models, launch props and retail display pieces, painted or metal plated, for studios and brand teams around the city.',
      },
    ],
    library:
      'The San Francisco Public Library runs a makerspace for teens at The Mix, and some Bay Area universities have maker labs. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'Delivering to an office in SoMa, a lab in South San Francisco or a venue for a launch? Give us the address and the in-hand date, and we’ll plan printing and delivery around it.',
    guide: '/guides/best-3d-printing-services-san-francisco/',
    faqs: [
      {
        question:
          'Can you 3D print enclosures and prototypes for a hardware startup?',
        answer:
          'Yes. We print looks-like and works-like enclosures, brackets and fit-check parts, then sand, prime or paint them for demos. When the design settles, we can run short production batches on our FDM print farm. Tell us if you need an NDA before you share files.',
      },
      {
        question:
          'Can you print a model or prop for a San Francisco product launch?',
        answer:
          'Yes. We print finished product models and launch props, sand them, and paint or metal plate them. Large pieces are printed in sections and assembled. Send the event date with your file.',
      },
      {
        question:
          'Do you work with clients in Oakland, Palo Alto and the rest of the Bay Area?',
        answer:
          'Yes, anywhere in the Bay Area. Delivery is quoted to your ZIP code with the print.',
      },
    ],
  },
  {
    slug: 'chicago',
    name: 'Chicago',
    state: 'Illinois',
    stateCode: 'IL',
    title: '3D Printing Chicago | Prototypes, Models & Props',
    description: `3D printing in Chicago for architects, agencies and brands: architectural models, prototypes, props and display pieces. No setup fee, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · Chicago, Illinois',
    areas:
      'Evanston, Oak Park, Schaumburg, Naperville and the rest of Chicagoland',
    hero: 'copperMolding',
    work: [
      'bronzeModel',
      'stairs',
      'building',
      'painting',
      'vitruvian',
      'goldSpheres',
    ],
    lead: 'PrintAWorld 3D prints architectural models, trade-show displays, product prototypes and props for Chicago architects, agencies and brands.',
    intro: [
      'Chicago is an architecture town, and architectural and scale models are one of our specialties: massing and site models, stair and facade studies, presentation models for competitions and client meetings. We also print prototypes and packaging models for the city’s food and consumer brands, and display pieces for shows at McCormick Place.',
      'For agencies, we make props and branded pieces on deadline. For artists, smooth sculptures ready for paint or plating. For product teams, prototypes and production parts. Every file gets a person and a written quote.',
    ],
    buyers: [
      {
        title: 'Architects and developers',
        body: 'Building, site and interior models at scale, in fine-detail resin or on our FDM printers, with large models printed in sections. Finishes run from clean white to bronze or copper.',
      },
      {
        title: 'Food, consumer and packaging brands',
        body: 'Package and product prototypes, oversized product replicas and painted display pieces for launches, sales meetings and retail tests.',
      },
      {
        title: 'Agencies and trade shows',
        body: 'Props, branded pieces and booth displays for shows and activations, from one hero piece to a run of giveaways.',
      },
    ],
    library:
      'The Chicago Public Library has a Maker Lab and YOUmedia spaces with 3D printers, and some colleges have labs. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'Delivering to McCormick Place, a hotel or an office in the Loop? Give us the address, the show or meeting date and any delivery window, and we’ll plan printing, finishing and delivery around it.',
    faqs: [
      {
        question: 'Do you 3D print architectural models for Chicago firms?',
        answer:
          'Yes, it’s one of our strengths. We print building, site and interior models at scale for presentations, competitions and displays, in fine-detail resin or on our FDM printers, and large models in sections. Send an STL, OBJ or 3DM export with the scale you need; price and schedule are quoted per project.',
      },
      {
        question:
          'Can you deliver a large display piece to a Chicago trade show?',
        answer:
          'Yes. Large pieces are printed in sections, assembled and finished, and we quote packing and delivery to the venue or your office with the print. Send the show dates early: large-format builds usually take 1–2 weeks, and bigger projects can take longer.',
      },
      {
        question:
          'Can you print packaging and product prototypes for Chicago brands?',
        answer:
          'Yes: bottle, container and product prototypes to test shape and size, and painted samples or oversized replicas for buyer meetings and launches. Send the file or a sketch, the quantity and the date.',
      },
    ],
  },
  {
    slug: 'new-jersey',
    name: 'New Jersey',
    state: 'New Jersey',
    stateCode: 'NJ',
    title: '3D Printing NJ | Prototypes, Custom Parts & Models',
    description: `3D printing for New Jersey businesses: prototypes, custom parts, product models and production runs. One print or a full run, no setup fee, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · New Jersey',
    areas: 'Jersey City, Hoboken, Newark, Edison, New Brunswick and Princeton',
    hero: 'cubes',
    work: [
      'containers',
      'dispenser',
      'prototype',
      'nylonParts',
      'hands',
      'pets',
    ],
    lead: 'PrintAWorld 3D prints prototypes, product models, display pieces and production parts for New Jersey companies, from pharma and consumer brands to agencies and architects.',
    intro: [
      'New Jersey buys 3D printing for pharma and life-science presentations, consumer-product and packaging prototypes, and agency work on both sides of the Hudson.',
      'We print one piece or a production run on our in-house FDM print farm, with no setup fee. A person reviews your file and sends a written quote, and you pick it up by appointment or we deliver it anywhere in the state.',
    ],
    buyers: [
      {
        title: 'Pharma and life sciences',
        body: 'Presentation models, device and packaging prototypes and lab fixtures for teams in central and northern New Jersey. Prototypes and models, not regulated production parts.',
      },
      {
        title: 'Consumer products and packaging',
        body: 'Bottle, cap, container and product prototypes to test shape and size, and painted samples for buyer meetings.',
      },
      {
        title: 'Agencies, architects and events',
        body: 'Props and display pieces for activations in Jersey City, Newark and Manhattan, and architectural models for design firms on both sides of the river.',
      },
    ],
    library:
      'Some New Jersey colleges and libraries have 3D printers, such as the MakerSpace in Hudson County Community College’s library. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'We deliver anywhere in New Jersey, from Jersey City and Hoboken to Princeton, and quote it to your ZIP code. You can also pick up by appointment at the studio named in your quote.',
    faqs: [
      {
        question: 'Can I pick up my 3D prints?',
        answer:
          'Yes, by appointment at the studio named in your quote. Send your file first, then confirm a time before you come. Or we deliver anywhere in New Jersey.',
      },
      {
        question: 'Do you deliver to Jersey City or Hoboken?',
        answer:
          'Yes, and anywhere else in New Jersey. Delivery is quoted to your ZIP code with the print.',
      },
      {
        question:
          'Can you print packaging prototypes for New Jersey consumer brands?',
        answer:
          'Yes: bottles, caps, containers and product shapes to test size and feel, and painted samples for buyer meetings. One prototype is fine, and so is a run for a consumer test.',
      },
    ],
  },
  {
    slug: 'las-vegas',
    name: 'Las Vegas',
    state: 'Nevada',
    stateCode: 'NV',
    title: '3D Printing Las Vegas | Props, Displays & Custom Parts',
    description: `3D printing in Las Vegas for shows, events and businesses: props, trade-show displays, product replicas and custom parts. No setup fee, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · Las Vegas, Nevada',
    areas: 'Henderson, Summerlin, Paradise and North Las Vegas',
    hero: 'spiderStage',
    work: [
      'painting',
      'goldAwards',
      'cupcakes',
      'bigPrint',
      'hands',
      'redCreature',
    ],
    lead: 'PrintAWorld 3D prints props, trade-show displays, product replicas, awards and custom parts for Las Vegas shows, events and businesses.',
    intro: [
      'Las Vegas runs on shows: conventions at the Las Vegas Convention Center, product launches on the Strip, residencies and events that need a prop, a replica or a set of awards by a fixed date. We print large pieces in sections, then sand, paint or metal plate them so they look finished on a stage or a booth.',
      'Every file gets a person and a written quote, and we deliver finished pieces to your office, warehouse or venue.',
    ],
    buyers: [
      {
        title: 'Trade shows and conventions',
        body: 'Oversized product replicas, booth display pieces and giveaways for shows, printed and painted in your brand colors.',
      },
      {
        title: 'Entertainment and hospitality',
        body: 'Props, set pieces and decor for shows, casinos, clubs and restaurants, painted or metal plated.',
      },
      {
        title: 'Event awards and merchandise',
        body: 'Gold-finished figure awards and branded pieces for events, from a handful to a full run on our FDM print farm.',
      },
    ],
    library:
      'Some Las Vegas libraries and colleges have 3D printers for members or students. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'Exhibiting at a show? Send the show dates, the delivery address and any delivery window from the organizer with your file, and set the in-hand date a few days before the show opens.',
    faqs: [
      {
        question:
          'Can you get a trade-show piece to Las Vegas in time for my show?',
        answer:
          'Usually, if you tell us the date up front. Many jobs print next day, but large or painted pieces take longer: large-format builds usually take 1–2 weeks. Send the show date with your file and the quote will confirm whether we can make it.',
      },
      {
        question: 'Do you 3D print props for Las Vegas shows and events?',
        answer: `Yes. We print props and set pieces, large ones in sections, and sand, paint or metal plate them. Send a file or a sketch; if you don’t have a 3D model, 3D modeling starts at $${pricing.modelingFrom}.`,
      },
      {
        question: 'Can you make awards for a Las Vegas event?',
        answer:
          'Yes. We 3D print figure awards and branded pieces, finished in gold, chrome or paint, and quote them per project. Send the design, the quantity and the event date.',
      },
    ],
  },
  {
    slug: 'san-antonio',
    name: 'San Antonio',
    state: 'Texas',
    stateCode: 'TX',
    title: '3D Printing San Antonio, TX | Custom Parts & Models',
    description: `3D printing in San Antonio for businesses, architects and event teams: custom parts, prototypes and display models. No setup fee, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · San Antonio, Texas',
    areas: 'New Braunfels, Schertz, Boerne and Alamo Heights',
    hero: 'bronzeDiorama',
    work: [
      'bronzeModel',
      'dino',
      'cupcakes',
      'miniatures',
      'supports',
      'elbows',
    ],
    lead: 'PrintAWorld 3D prints custom parts, prototypes, architectural and display models, and event pieces for San Antonio businesses.',
    intro: [
      'San Antonio work is varied: healthcare and bioscience teams with a device or packaging prototype, manufacturers who need a fixture or a replacement part checked for fit, architects and preservation groups who want a model of a building, and tourism and event teams who need a prop for the River Walk or a convention.',
      'A person reviews the file and sends a written quote; we print it in-house, finish what needs finishing, and deliver it to your site.',
    ],
    buyers: [
      {
        title: 'Healthcare and bioscience',
        body: 'Device and packaging prototypes and presentation models for teams around the South Texas Medical Center. Prototypes and models, not regulated production.',
      },
      {
        title: 'Manufacturing and industrial',
        body: 'Fit-check parts, fixtures to try out and replacement-part prototypes, printed in nylon, ABS or stainless steel.',
      },
      {
        title: 'Architecture, heritage and tourism',
        body: 'Building and site models, bronze-finished display models and event props for museums, hotels and tour operators.',
      },
    ],
    library:
      'Some San Antonio libraries and colleges have makerspaces with 3D printers. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'We deliver anywhere in the San Antonio area, from downtown to New Braunfels and Boerne, and quote delivery to your ZIP code with the print.',
    faqs: [
      {
        question: 'Can I order a single 3D print in San Antonio?',
        answer: `Yes. There’s no minimum and no setup fee, and simple prints from a ready file start around $${pricing.printFrom}. For a quick hobby print, a makerspace or a hobbyist will usually cost less.`,
      },
      {
        question: 'Can you 3D print architectural or historic building models?',
        answer: `Yes. We print building and site models at scale and finish them in white, paint or a bronze look. Send an STL, OBJ or 3DM file and the scale; if you only have drawings or photos, 3D modeling starts at $${pricing.modelingFrom}.`,
      },
      {
        question:
          'Can you print fixtures or replacement-part prototypes for San Antonio manufacturers?',
        answer: `Yes. We print fit-check parts and fixtures to try out in nylon, ABS or stainless steel. To copy a worn or discontinued part, we can 3D scan it first; scanning starts at $${pricing.scanningFrom}. These are prototypes, not certified production parts.`,
      },
    ],
  },
  {
    slug: 'san-jose',
    name: 'San Jose',
    state: 'California',
    stateCode: 'CA',
    title: '3D Printing Service San Jose, CA | Prototypes & Parts',
    description: `3D printing service for San Jose and Silicon Valley: enclosure prototypes, consumer-product models and short production runs. No setup fee, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · San Jose & Silicon Valley',
    areas: 'Santa Clara, Sunnyvale, Mountain View, Milpitas and Cupertino',
    hero: 'earbuds',
    work: [
      'appliance',
      'nylonParts',
      'gear',
      'containers',
      'supports',
      'diorama',
    ],
    lead: 'PrintAWorld 3D prints enclosure prototypes, consumer-product models, fixtures to try out and short production runs for San Jose and Silicon Valley companies.',
    intro: [
      'Silicon Valley teams already have printers on the desk. They come to us when a part has to look like a product, when they need fifty instead of one, or when the build is too big for the lab machine: a painted enclosure for a launch photo, a set of colorways for a design review, a full-size housing for a trade show.',
      'Every job gets a written quote from a person before anything is made, and finished parts are delivered to your office or lab.',
    ],
    buyers: [
      {
        title: 'Consumer electronics',
        body: 'Looks-like enclosures, colorway sets and accessory models, sanded and painted for design reviews, photo shoots and retail tests.',
      },
      {
        title: 'Semiconductor and hardware engineering',
        body: 'Fit-check parts, cable guides, covers and test fixtures to try in the lab, printed in nylon, ABS or resin, or stainless steel when it has to be metal.',
      },
      {
        title: 'Automotive and robotics teams',
        body: 'Housings, brackets and scale models for EV, autonomy and robotics programs, plus short runs on our FDM print farm.',
      },
    ],
    library:
      'Some Silicon Valley libraries, colleges and company labs have 3D printers. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'We deliver to offices, labs and loading docks across the valley. Tell us whether it goes to a lobby or a lab, and the date it’s needed.',
    faqs: [
      {
        question:
          'Why use a 3D printing service if we have printers in the office?',
        answer:
          'Desk printers are great for quick checks. Send us the parts that need to look finished, the runs that would tie up your machines for days, large builds, resin detail, stainless steel, or sanding, painting and plating.',
      },
      {
        question:
          'Can you print product colorways and enclosures for a design review?',
        answer:
          'Yes. We print enclosures and accessory models, then sand, prime and paint them in the colors you need. Send the file, color references and the date; finishing is quoted with the print.',
      },
      {
        question:
          'Can you run a short production batch for a Silicon Valley hardware team?',
        answer:
          'Yes. Large production orders run on our in-house FDM print farm, and we quote runs per piece, with no setup fee. Tell us the quantity, the material and the date; sanding, painting and packing are quoted with the print.',
      },
    ],
  },
  {
    slug: 'boston',
    name: 'Boston',
    state: 'Massachusetts',
    stateCode: 'MA',
    title: '3D Printing Boston, MA | Prototypes, Models & Parts',
    description: `3D printing service for Boston and Cambridge: robotics and hardware prototypes, research parts and architectural models. No setup fee, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · Boston, Massachusetts',
    areas: 'Cambridge, Somerville, Waltham, Quincy and Worcester',
    hero: 'greyParts',
    work: [
      'spiderRobot',
      'nylonParts',
      'architecture',
      'prototype',
      'crab',
      'gear',
    ],
    lead: 'PrintAWorld 3D prints robotics and hardware prototypes, research parts, architectural models and display pieces for Boston and Cambridge teams.',
    intro: [
      'Boston’s 3D printing buyers are engineers and researchers more than hobbyists: robotics companies that need a housing or a gripper finger to test, biotech and university labs that need a holder or a fixture, architects who need a presentation model. Most have printers already; they send us the parts that need a different process, a finish, or more of them.',
      'Every file is reviewed by a person and quoted in writing before printing, and parts are delivered to your lab, office or campus.',
    ],
    buyers: [
      {
        title: 'Robotics and hardware',
        body: 'Housings, brackets, gripper and end-effector prototypes and short runs, in nylon, ABS, resin or stainless steel.',
      },
      {
        title: 'Biotech, research and universities',
        body: 'Lab fixtures, holders and research parts to try out, and presentation models for labs in Cambridge, Kendall Square and the Longwood area. Prototypes and models, not regulated medical production.',
      },
      {
        title: 'Architects and designers',
        body: 'Building, site and interior models at scale for presentations, reviews and competitions, printed in sections when they’re large.',
      },
    ],
    library:
      'Boston-area universities run maker labs for students and staff, and some libraries have 3D printers. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'We deliver to offices, labs and campuses across Greater Boston. Tell us the building and the date, and whether a receiving dock needs notice.',
    faqs: [
      {
        question: 'Can I rent a 3D printer from you in Boston?',
        answer:
          'We don’t rent printers. We run the printing for you: send the file and we’ll quote the finished part. If you’d rather run a printer yourself, a university maker lab or a makerspace is the better fit.',
      },
      {
        question: 'Do you 3D print robotics prototypes?',
        answer:
          'Yes. We print housings, brackets, gripper fingers and other test parts in nylon, ABS or resin, and stainless steel by selective laser melting in a 250 × 250 × 300 mm build area. Short runs go on our FDM print farm. Tell us how the part is loaded and we’ll suggest a material.',
      },
      {
        question: 'Can Boston research labs order a single custom part?',
        answer: `Yes. There’s no minimum order and no setup fee, so a single fixture, holder or test part is fine; simple prints start around $${pricing.printFrom}. Send the file and tell us how the part will be used.`,
      },
    ],
  },
  {
    slug: 'los-angeles',
    name: 'Los Angeles',
    state: 'California',
    stateCode: 'CA',
    title: '3D Printing Los Angeles | Large-Format, Resin & Sculpture',
    description: `3D printing in Los Angeles for artists, studios and brands: large-format sculptures, resin props, product replicas and art pieces. No setup fee, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · Los Angeles, California',
    areas: 'Burbank, Culver City, Santa Monica, Pasadena and Long Beach',
    clients: true,
    hero: 'chair',
    work: ['bust', 'failArt', 'goldTrophies', 'earrings', 'painting', 'busts'],
    lead: 'PrintAWorld 3D prints large-format sculptures, resin props, product replicas and art pieces for Los Angeles artists, studios and brands.',
    intro: [
      'LA work is mostly about how a piece looks: a sculpture for a gallery show, a prop for a shoot, a chrome chair for an installation, a gold award for a creator event, a product replica for a launch. We print in fine-detail resin or large-format, in sections when it’s big, and finish it ourselves: sanding, painting and metal plating.',
      'LA clients use us because we reply quickly and get projects done in a matter of days, and the pieces arrive finished: sanded, painted or plated by the team that printed them.',
    ],
    buyers: [
      {
        title: 'Film, TV and content production',
        body: 'Props, replicas and set pieces, painted or plated for the camera, from one hero prop to a batch.',
      },
      {
        title: 'Artists and galleries',
        body: 'Sculptures and art furniture printed in resin or large-format, sanded and finished in paint or chrome, gold or copper plating.',
      },
      {
        title: 'Fashion, beauty and consumer brands',
        body: 'Jewelry and accessory prototypes, oversized product replicas and display pieces for launches and pop-ups.',
      },
    ],
    library:
      'The Los Angeles Public Library offers 3D printing at some branches, and some colleges have maker labs. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'We deliver to studios, sets and offices across LA County. Tell us the address, any loading instructions and the in-hand date.',
    guide: '/guides/best-3d-printing-services-los-angeles/',
    faqs: [
      {
        question: 'Can you 3D print film and TV props for LA productions?',
        answer: `Yes. We print props, replicas and set pieces, then sand, paint or metal plate them for the camera. Send a file, a sketch or reference photos and the shoot date; if you need a 3D model made, modeling starts at $${pricing.modelingFrom}.`,
      },
      {
        question: 'Do you offer resin (SLA) 3D printing for LA clients?',
        answer:
          'Yes. SLA resin prints have fine detail and a smooth surface for sculptures, props, jewelry prototypes and models. Standard and Somos resins are the usual choices, and Somos WaterClear for clear parts. See SLA resin 3D printing for details.',
      },
      {
        question: 'Can you print a large sculpture or prop for Los Angeles?',
        answer:
          'Yes. We print large sculptures and props in sections and assemble them; finished projects have reached 6 feet and more. We sand, paint or metal plate them, and quote packing and delivery to your site with the print.',
      },
    ],
  },
  {
    slug: 'indianapolis',
    name: 'Indianapolis',
    state: 'Indiana',
    stateCode: 'IN',
    title: '3D Printing Indianapolis, IN | Custom Parts & Prototypes',
    description: `3D printing in Indianapolis for motorsport, life-science and manufacturing teams: custom parts, prototypes and scale models. No setup fee, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · Indianapolis, Indiana',
    areas: 'Carmel, Fishers, Noblesville, Greenwood and Speedway',
    hero: 'carFrame',
    work: [
      'elbows',
      'greyParts',
      'nylonParts',
      'gear',
      'prototype',
      'supports',
    ],
    lead: 'PrintAWorld 3D prints custom parts, prototypes, scale models and display pieces for Indianapolis motorsport, life-science and manufacturing teams.',
    intro: [
      'Indianapolis has two big reasons to 3D print. Motorsport: race teams and suppliers around Speedway who need scale models, fit-check parts and show pieces. Life sciences: pharma and device teams who need prototypes and presentation models. Add the logistics and manufacturing companies around the city, and the conventions downtown.',
      'You send the file, a person checks it and quotes it, and we print, finish and deliver it to your shop or office.',
    ],
    buyers: [
      {
        title: 'Motorsport and automotive',
        body: 'Scale car and body models, aero and bracket prototypes to check fit, and painted display pieces for sponsors and shows. Prototypes and models, not race-certified parts.',
      },
      {
        title: 'Life sciences and pharma',
        body: 'Device, packaging and dispenser prototypes and presentation models. Prototypes only, not regulated production parts.',
      },
      {
        title: 'Manufacturing, logistics and conventions',
        body: 'Fixtures and covers to try on the line, short runs on our FDM print farm, and booth pieces for shows at the Indiana Convention Center.',
      },
    ],
    library:
      'Some Indianapolis libraries and colleges have 3D printers or maker labs. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'We deliver to shops, offices and venues across Central Indiana, from Speedway to Carmel and Fishers, and quote delivery to your ZIP code.',
    faqs: [
      {
        question: 'Do you rent 3D printers in Indianapolis?',
        answer:
          'No, we don’t rent printers; we print the parts for you. Send the file and we’ll quote the finished part, with no setup fee. To run a printer yourself, a library or college maker lab is the better fit.',
      },
      {
        question: 'Can you 3D print a scale race car or vehicle model?',
        answer:
          'Yes. We print scale vehicle and body models in resin or large-format, in sections when they’re big, then sand and paint them for display. Send a CAD or STL file and the scale. These are display models, not functional race parts.',
      },
      {
        question:
          'Can you print booth pieces for a show at the Indiana Convention Center?',
        answer:
          'Yes. We print display models, replicas and branded pieces, large ones in sections, and finish them in paint or metal plating. Send the show date and the delivery address with your file.',
      },
    ],
  },
  {
    slug: 'minneapolis',
    name: 'Minneapolis',
    state: 'Minnesota',
    stateCode: 'MN',
    title: '3D Printing Minneapolis, MN | Prototypes & Models',
    description: `3D printing in Minneapolis–St. Paul for medical-device, retail and consumer brands: prototypes, product models and production runs. No setup fee, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · Minneapolis–St. Paul, Minnesota',
    areas: 'St. Paul, Bloomington, Edina, Eden Prairie and Plymouth',
    hero: 'containers',
    work: ['dispenser', 'cubes', 'appliance', 'gear', 'pets', 'diorama'],
    lead: 'PrintAWorld 3D prints prototypes, product and packaging models, display pieces and production parts for Twin Cities companies, from medical-device teams to retail and food brands.',
    intro: [
      'The Twin Cities have a large cluster of medical-device companies, plus big retail, food and consumer brands. That means a lot of prototypes: a handheld device housing to test grip, a dispenser to check, a package to put in front of buyers, a display for a store test.',
      'A person reviews your file and sends a written quote; we print and finish it in-house and deliver it to your office or site in the Twin Cities.',
    ],
    buyers: [
      {
        title: 'Medical-device teams',
        body: 'Housings, handles and enclosures to test fit and feel, and presentation models. Prototypes only, not regulated medical production.',
      },
      {
        title: 'Retail, food and consumer brands',
        body: 'Packaging and product prototypes, store display pieces and oversized replicas, painted for buyer meetings and photo shoots.',
      },
      {
        title: 'Agencies and manufacturers',
        body: 'Props for campaigns, short production runs on our FDM print farm, and fixtures to try on the line.',
      },
    ],
    library:
      'The University of Minnesota runs maker labs for its students, and some libraries and makerspaces in the Twin Cities have 3D printers. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'We deliver anywhere in the Twin Cities and quote it to your ZIP code. In winter, leave a little extra time for weather when you set the in-hand date.',
    faqs: [
      {
        question: 'Do you rent 3D printers in the Twin Cities?',
        answer:
          'We don’t rent printers. We print the parts for you, from one prototype to a production run, with no setup fee. If you want to run a printer yourself, a makerspace or campus lab is the better fit.',
      },
      {
        question: 'Can you 3D print medical-device prototypes?',
        answer:
          'Yes, as prototypes: housings, handles, enclosures and presentation models in resin, nylon or ABS. We don’t make regulated, patient-contact or production medical parts, and we don’t claim medical certifications.',
      },
      {
        question:
          'Can you print packaging and display prototypes for Twin Cities retail brands?',
        answer:
          'Yes: product and packaging prototypes, store display pieces and oversized replicas, painted for buyer meetings and photo shoots. Send the file or a sketch, the quantity and the date.',
      },
    ],
  },
  {
    slug: 'baltimore',
    name: 'Baltimore',
    state: 'Maryland',
    stateCode: 'MD',
    title: '3D Printing Baltimore, MD | Parts & Prototypes',
    description: `3D printing in Baltimore for labs, architects, artists and businesses: parts, prototypes, models and sculptures. One print to production runs, from $${pricing.printFrom}.`,
    eyebrow: '3D printing service · Baltimore, Maryland',
    areas: 'Towson, Columbia, Ellicott City, Owings Mills and Annapolis',
    hero: 'diorama',
    work: ['building', 'containers', 'dino', 'clocks', 'hands', 'sculpture'],
    lead: 'PrintAWorld 3D prints prototypes, research parts, architectural models and art pieces for Baltimore labs, firms, artists and businesses.',
    intro: [
      'Baltimore buys 3D printing for research and for presentation: university and hospital labs that need a fixture or a model, architects and developers working on the harbor and the neighborhoods, artists and designers, and port and manufacturing companies that need a part checked before it’s made in metal.',
      'A person reviews your file and sends a written quote, and we print, finish and deliver it to your lab, office or studio.',
    ],
    buyers: [
      {
        title: 'Research labs and life sciences',
        body: 'Fixtures, holders and research parts to try out, and presentation models. Prototypes and models, not regulated medical production.',
      },
      {
        title: 'Architects and developers',
        body: 'Building, site and interior models at scale, in white resin or with a painted or metal-plated finish.',
      },
      {
        title: 'Artists, designers and makers',
        body: 'Sculptures and art pieces printed in resin and finished in paint or metal plating, for shows and commissions.',
      },
    ],
    library:
      'Some Baltimore libraries, colleges and art schools have 3D printers or maker labs. Cheapest for small, simple parts; you usually prepare the file and run the print yourself.',
    delivery:
      'We deliver anywhere in the Baltimore area and across Maryland, quoted to your ZIP code with the print.',
    faqs: [
      {
        question: 'Can you print architectural models for Baltimore firms?',
        answer:
          'Yes, it’s one of our strengths. We print building, site and interior models at scale for presentations, competitions and displays, and large models in sections. Send an STL, OBJ or 3DM export with the scale; price and schedule are quoted per project.',
      },
      {
        question: 'Can artists order 3D printed sculptures?',
        answer:
          'Yes. We print sculptures in fine-detail resin or large-format and finish them in paint, or plate them in gold, chrome, copper or bronze. Plating coats the surface; the piece isn’t solid metal. Send the file or photos of your maquette.',
      },
      {
        question: 'Can Baltimore labs order fixtures and research parts?',
        answer:
          'Yes: holders, fixtures and test parts in resin, nylon or ABS, from one piece to a short run. These are research prototypes, not regulated medical parts.',
      },
    ],
  },
]

// The base shared by every city page, filled in with the city's own block.
const buildCity = (city) => {
  const { name } = city
  const path = `/3d-printing-service-${city.slug}/`
  const answer = [
    city.lead,
    city.clients ? `We already work with many clients in ${name}.` : '',
    `Send an STL, OBJ or STEP file, or a sketch or photo, and a person reviews it and sends a written quote. We print it in-house on resin, FDM and metal printers, finish it if you need, and deliver it to your site in ${name}.`,
    `From one print to a production run, with no setup fee: projects start around $${pricing.printFrom}.`,
  ]
    .filter(Boolean)
    .join(' ')

  return {
    slug: city.slug,
    name,
    state: city.state,
    stateCode: city.stateCode,
    clients: Boolean(city.clients),
    pricing,
    page: {
      path,
      title: city.title,
      description: city.description,
      heading: `3D Printing Service in ${name}`,
      eyebrow: city.eyebrow,
      answer,
      hero: city.hero,
      reviewedDate,
    },
    photos,
    work: city.work,
    intro: city.intro,
    buyers: city.buyers,
    services: printing.services,
    materials: printing.materials,
    glance: [
      ['Serving', `${name}, ${city.areas}`],
      [
        'Who it’s for',
        'Businesses, brands, agencies and creative or production work',
      ],
      ['Production', 'In-house, with a person reviewing every file'],
      [
        'Delivery',
        `To your office, lab, venue or site in ${name}, quoted to your ZIP code`,
      ],
      [
        'Order size',
        'One print to large production runs on our FDM print farm',
      ],
      ['Setup fee', 'None for 3D printing'],
      [
        'Price',
        `From about $${pricing.printFrom} for a simple print from a ready file`,
      ],
      [
        'How you get a price',
        'A written quote after a person reviews your file',
      ],
      [
        'Processes',
        'An FDM print farm, SLA resin, large-format and stainless-steel metal (SLM) 3D printing',
      ],
      [
        'Materials',
        'Standard and Somos resins, Somos WaterClear, ABS, Nylon PA12, stainless steel',
      ],
      [
        'Largest size',
        '6 feet and more, printed in sections. Metal: 250 × 250 × 300 mm.',
      ],
      [
        'Turnaround',
        'Next-day printing available for many jobs. Large-format builds usually take 1–2 weeks. Your quote confirms the delivery date.',
      ],
      ['Files', 'STL, OBJ, STEP, STP or 3DM. No file? Photos or a sketch.'],
      ['Also', '3D modeling, 3D scanning, sanding, painting and metal plating'],
    ],
    options: [
      {
        label: 'O1',
        title: 'A library, college or makerspace',
        body: city.library,
      },
      {
        label: 'O2',
        title: 'An instant-quote website',
        body: 'Upload a print-ready file and get a price automatically. Good for standard engineering parts; file repair and painting usually aren’t included.',
      },
      {
        label: 'O3',
        title: 'A 3D printing studio like ours',
        body: `A person checks your file, helps pick the material, and can model, finish, paint or plate the part. Best for business, brand and production work, delivered to your site in ${name}.`,
      },
    ],
    fit: {
      good: printing.fit.good.filter((item) => !/courier/i.test(item)),
      other: [
        ...printing.fit.other,
        'A print you need in hand today: same-day isn’t standard, so a makerspace or a hobbyist is the better choice.',
      ],
    },
    steps: [
      printing.steps[0],
      printing.steps[1],
      {
        title: 'We print it',
        body: 'Once you approve the quote, we print it in-house and remove supports.',
      },
      printing.steps[3],
      {
        title: 'Delivered to your site',
        body: `We pack it for the part and deliver it to your office, lab, venue or site in ${name}.`,
      },
    ],
    prices: printing.prices.map(([label, price]) =>
      /^Shipping/.test(label)
        ? [`Delivery in ${name}`, 'Quoted to your ZIP']
        : [label, price]
    ),
    delivery: city.delivery,
    guide: city.guide || null,
    checklist: [
      ...printing.checklist.slice(0, -1),
      `Your ${name} delivery ZIP code and deadline`,
    ],
    faqs: [...city.faqs, ...baseFaqs(name)],
    cities: printing.cities.filter(([, cityPath]) => cityPath !== path),
  }
}

const cities = Object.fromEntries(
  cityList.map((city) => [city.slug, buildCity(city)])
)

// Contentful page slugs that gatsby-node must not render any more.
const citySlugs = cityList.map((city) => `3d-printing-service-${city.slug}`)

module.exports = { cities, citySlugs, reviewedDate }
